/* eslint-disable @next/next/no-img-element */
'use client'

import {
  DEFAULT_THUMBNAIL_ROUNDED,
  THUMBNAIL_RADIUS_PC,
  THUMBNAIL_RADIUS_SP,
  thumbnailRadius,
} from '@/components/scenario/thumbnailCorner'
import { DEFAULT_EMBED_FONT, type EmbedFont } from '@/components/scenario/embedFont'
import { createThumbnailHoverHandlers } from '@/components/video/HoverAnimatedThumbnail'
import { useHoverPrefetch } from '@/hooks/useHoverPrefetch'
import { useViewportVideoPrefetch } from '@/hooks/useViewportVideoPrefetch'
import { trackSessionStart } from '@/lib/analytics'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { FC, useEffect, useRef, useState } from 'react'
import { useBridge } from './bridge/BridgeContext'
import { VideoModal } from './modal'
import { useShadowHostPortal } from './shadow-host-portal/ShadowHostPortalContext'
import UnifiedPlayMark from './UnifiedPlayMark'

export interface StandalonePlayerProps {
  nodes: ScenarioNodeWithVideoUrl[]
  videoOrder: string[]
  /** 単独表示する動画 ID。クリックするとモーダルでプレイリスト全体を視聴可能 */
  videoId: string
  /** サムネイルの角丸。false なら直角（先方サイトのデザインに合わせる設定） */
  thumbnailRounded?: boolean
  /** 視聴モーダルのフォント（プレイリスト単位の設定） */
  embedFont?: EmbedFont
  scenarioId?: string
}

const StandalonePlayer: FC<StandalonePlayerProps> = ({
  nodes,
  videoOrder,
  videoId,
  thumbnailRounded = DEFAULT_THUMBNAIL_ROUNDED,
  embedFont = DEFAULT_EMBED_FONT,
  scenarioId,
}) => {
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number | null>(null)
  const [isPiPActive, setIsPiPActive] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasTrackedRef = useRef(false)
  const { schedulePrefetch, cancelPrefetch } = useHoverPrefetch()

  // SESSION_START を viewport に入った時点で 1 回だけ計測する
  useEffect(() => {
    if (!scenarioId || hasTrackedRef.current) return
    const el = containerRef.current
    /* v8 ignore next */
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true
          trackSessionStart(scenarioId)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [scenarioId])

  // videoOrder が指定されていれば順序通り、無ければ nodes 順
  const orderedNodes =
    videoOrder.length > 0
      ? videoOrder
          .map((id) => nodes.find((node) => node.videoId === id))
          .filter((node): node is ScenarioNodeWithVideoUrl => node !== undefined)
      : nodes

  // 単独表示する node を特定。指定 videoId が見つからなければ先頭を fallback
  const targetNode = orderedNodes.find((n) => n.videoId === videoId) ?? orderedNodes[0]
  const targetIndex = targetNode ? orderedNodes.findIndex((n) => n.videoId === targetNode.videoId) : 0
  useViewportVideoPrefetch(containerRef, targetNode?.videoUrl)

  const bridge = useBridge()
  // 早期リターン (targetNode 不在) は描画前の return null で対応するため、handleClick 内では存在前提
  const handleClick = () => {
    /* v8 ignore next */
    if (!targetNode) return
    if (bridge) {
      bridge.sendOpenIntent(targetNode.videoId)
      return
    }
    setSelectedVideoIndex(targetIndex)
  }

  const portal = useShadowHostPortal()
  const isOverlayActive = selectedVideoIndex !== null || isPiPActive
  useEffect(() => {
    portal?.setPortalActive(isOverlayActive)
  }, [isOverlayActive, portal])
  useEffect(() => {
    return () => {
      portal?.setPortalActive(false)
    }
  }, [portal])

  const handleCloseModal = () => {
    setSelectedVideoIndex(null)
  }

  const handleRequestOpen = (videoIndex: number) => {
    setSelectedVideoIndex(videoIndex)
  }

  if (!targetNode) {
    return null
  }

  return (
    <>
      <style>{`
        .standalone-card {
          border-radius: ${thumbnailRadius(thumbnailRounded, THUMBNAIL_RADIUS_SP)};
        }
        @media (min-width: 768px) {
          .standalone-card {
            border-radius: ${thumbnailRadius(thumbnailRounded, THUMBNAIL_RADIUS_PC)};
          }
        }
        .standalone-card .play-overlay {
          opacity: 1 !important;
          background: transparent;
        }
        .standalone-card:hover .play-overlay {
          background: rgba(0, 0, 0, 0.3);
        }
      `}</style>

      <div
        ref={containerRef}
        className={`w-full max-w-[280px] mx-auto p-0 pb-1 ${
          isOverlayActive ? 'invisible' : ''
        }`}
      >
        <div
          className="standalone-card group relative cursor-pointer overflow-hidden bg-gray-100 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
          onClick={handleClick}
          onPointerEnter={() => schedulePrefetch(targetNode.videoUrl)}
          onPointerLeave={cancelPrefetch}
        >
          <div className="relative w-full" style={{ paddingBottom: '100%' }}>
            {targetNode.thumbnailUrl ? (
              <>
                <img
                  src={targetNode.thumbnailUrl}
                  alt={targetNode.video?.title ?? '動画'}
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  {...createThumbnailHoverHandlers(targetNode.thumbnailUrl, targetNode.animatedThumbnailUrl)}
                />
                <div className="play-overlay pointer-events-none absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-0 transition-all duration-200">
                  <UnifiedPlayMark />
                </div>
              </>
            ) : (
              <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center bg-gray-500">
                <UnifiedPlayMark />
                <p className="mt-2 text-[14px] text-gray-600">動画</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <VideoModal
        nodes={nodes}
        videoOrder={videoOrder}
        initialVideoIndex={selectedVideoIndex ?? 0}
        isOpen={selectedVideoIndex !== null}
        onClose={handleCloseModal}
        onRequestOpen={handleRequestOpen}
        onPiPActiveChange={setIsPiPActive}
        scenarioId={scenarioId}
        embedFont={embedFont}
      />
    </>
  )
}

export default StandalonePlayer
